import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, type MouseEvent } from "react";
import { buildToc, sanitizeCourseHtml, type TocItem } from "../html";

export interface DocumentHandle {
  scrollTo: (id: string) => void;
}

interface Props {
  html: string;
  onToc: (toc: TocItem[]) => void;
  onActiveChange: (id: string | null) => void;
}

const ACTIVE_OFFSET = 96; // px below the top of the pane that counts as "reading here"

/** The course document, rendered in its own scroll pane. Reports TOC and reading position. */
export const DocumentView = forwardRef<DocumentHandle, Props>(function DocumentView(
  { html, onToc, onActiveChange },
  ref,
) {
  const paneRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const safeHtml = useMemo(() => sanitizeCourseHtml(html), [html]);
  const lastActive = useRef<string | null>(null);

  const updateActive = useCallback(() => {
    const pane = paneRef.current;
    const body = bodyRef.current;
    if (!pane || !body) return;
    const top = pane.getBoundingClientRect().top + ACTIVE_OFFSET;
    let current: string | null = null;
    for (const el of body.querySelectorAll<HTMLElement>("h2[id], h3[id]")) {
      if (el.getBoundingClientRect().top <= top) current = el.id;
      else break;
    }
    if (!current) current = body.querySelector<HTMLElement>("h2[id]")?.id ?? null;
    if (current !== lastActive.current) {
      lastActive.current = current;
      onActiveChange(current);
    }
  }, [onActiveChange]);

  useImperativeHandle(ref, () => ({
    scrollTo(id: string) {
      const target = bodyRef.current?.querySelector<HTMLElement>(`#${CSS.escape(id)}`);
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      history.replaceState(null, "", `#${id}`);
    },
  }));

  // Rebuild the TOC whenever the document changes, keeping the reading position.
  useLayoutEffect(() => {
    if (bodyRef.current) onToc(buildToc(bodyRef.current));
    updateActive();
  }, [safeHtml, onToc, updateActive]);

  // Deep link: /courses/x#some-heading on first load.
  useEffect(() => {
    const id = decodeURIComponent(location.hash.slice(1));
    if (id) bodyRef.current?.querySelector<HTMLElement>(`#${CSS.escape(id)}`)?.scrollIntoView();
    // only on mount
  }, []);

  useEffect(() => {
    const pane = paneRef.current;
    if (!pane) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateActive);
    };
    pane.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      pane.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [updateActive]);

  // In-document links (#anchor) scroll the pane instead of jumping the page.
  function onClick(e: MouseEvent) {
    const a = (e.target as HTMLElement).closest("a");
    if (!a) return;
    const href = a.getAttribute("href") ?? "";
    if (href.startsWith("#")) {
      e.preventDefault();
      const target = bodyRef.current?.querySelector<HTMLElement>(`#${CSS.escape(href.slice(1))}`);
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (/^https?:/i.test(href)) {
      a.target = "_blank";
      a.rel = "noopener noreferrer";
    }
  }

  return (
    <div className="doc-pane" ref={paneRef}>
      <div className="doc" ref={bodyRef} onClick={onClick} dangerouslySetInnerHTML={{ __html: safeHtml }} />
    </div>
  );
});
